import { InsightTag, InsightView } from '@ndvr/customer-api';

import {
    householdGuidComponent,
    opportunityGuidComponent,
    personOrgGuidComponent,
} from '@/views/ai-chat/prompt-components';

// The reply classifier's justification for surfacing an R2/R3 insight, woven into the draft prompt so the AI knows
// what open question or request the advisor is responding to. Empty when the producer recorded no reason.
const whySurfaced = (reason: string | undefined): string =>
    reason?.trim() ? `\n\n**Why this was flagged:** ${reason.trim()}` : '';

export const buildInsightPrompt = (insight: InsightView): string | undefined => {
    const { payload } = insight;
    switch (payload.type) {
        case InsightTag.E1:
            return `Draft a warm, personalized **birthday email** for ${personOrgGuidComponent(payload.subjectPersonOrgGUID)}.

The email should feel genuine — reference any relevant context about the client's interests, family, or recent life events. Keep it brief and heartfelt.`;
        case InsightTag.E2:
            return `Draft a thoughtful **age milestone outreach email** for ${personOrgGuidComponent(payload.subjectPersonOrgGUID)}.

**Planning context:** ${payload.planningContext}

Acknowledge the milestone warmly and weave in any relevant planning considerations. Keep the tone personal, not transactional.`;
        case InsightTag.E3:
            return `Draft a **re-engagement email** for ${householdGuidComponent(payload.householdGUID)}.
**Context:** Last contacted **${payload.daysSinceLastContact} days ago** (${payload.lastContactedAt}).

The email should feel natural and reconnecting — reference any relevant recent events or context to warm up the outreach. Avoid sounding like a check-in for its own sake.`;
        case InsightTag.E4:
            return `Draft a thoughtful **new child outreach email** for ${householdGuidComponent(payload.householdGUID)}.

- **Child name:** ${payload.childName}
- **Approximate event date:** ${payload.approximateEventDate}
- **Evidence:** ${payload.evidence}${whySurfaced(payload.reason)}

Convey genuine congratulations and, where appropriate, mention relevant planning considerations (education savings, life insurance, estate updates). Keep it warm and human-first.`;
        case InsightTag.N1:
            return `Review the stale opportunity **"${payload.opportunityName}"** for ${householdGuidComponent(payload.householdGUID)} and suggest **concrete next steps** to move it forward.

- **Days inactive:** ${payload.daysInactive}
- **Current stage:** ${payload.stage}

Identify what may be blocking progress and recommend 2–3 specific, actionable next steps.`;
        case InsightTag.N2:
            return `Review the opportunity ${opportunityGuidComponent(payload.opportunityGUID)} for ${householdGuidComponent(payload.householdGUID)} and explain the **recommended next action**.

- **Opportunity:** ${payload.opportunityName}
- **Stage:** ${payload.stage}
- **Value:** ${payload.value}
- **Recommendation:** ${payload.recommendedAction}

Explain *why* this action is recommended and what a successful outcome looks like.`;
        case InsightTag.N4:
            return `Draft a warm **referral outreach email** for ${householdGuidComponent(payload.householdGUID)}.

- **Context:** ${payload.contextBrief}
- **Suggested referral ask:** ${payload.suggestedReferralAsk}${whySurfaced(payload.reason)}

Open by acknowledging the recent positive moment, then work in the referral ask naturally and graciously. Keep it personal and never pushy — the ask should feel like a genuine extension of the relationship.`;
        case InsightTag.N6:
            return `Prepare a **pre-meeting pitch brief** for prospect ${householdGuidComponent(payload.householdGUID)} ahead of the next meeting on opportunity ${opportunityGuidComponent(payload.opportunityGUID)}.

- **Value proposition:** ${payload.valueProposition}
- **Problems they've expressed:** ${payload.expressedProblems}
- **How we help:** ${payload.capabilitiesAlignment}
- **Proof points:** ${payload.suggestedProofPoints}
- **Based on:** ${payload.interactionCount} logged interactions${whySurfaced(payload.reason)}

Turn this into a concise, personalized pitch the advisor can walk into the meeting with: lead with the prospect's own expressed problems, connect each to how the firm helps, and back it up with the proof points. Keep it specific to this prospect — no generic boilerplate.`;
        case InsightTag.R2:
            return `A client message to ${householdGuidComponent(payload.householdGUID)} was flagged about **${payload.waitingDays} days** ago as possibly awaiting your reply.${whySurfaced(payload.reason)}

First open the thread and confirm the client's question is still open — that they haven't since replied, it wasn't already answered, and it wasn't likely handled another way (a call or meeting). If it looks resolved or the conversation has moved on, say so briefly instead of drafting. Otherwise, draft a clear, helpful reply that addresses the open question directly. Keep the tone warm and professional.`;
        case InsightTag.R3:
            return `A message to ${householdGuidComponent(payload.householdGUID)} was flagged about **${payload.waitingDays} days** ago as possibly awaiting a client reply.${whySurfaced(payload.reason)}

First open the thread and confirm it's still the latest activity and genuinely unanswered — that the client hasn't since replied, the conversation hasn't moved on, and it wasn't likely handled off-channel (a call or meeting). If it looks resolved or no longer relevant, say so briefly instead of forcing a draft. Otherwise, write a brief, friendly nudge that re-surfaces the original ask without sounding pushy — reference the prior message and make it easy to respond.`;
        default:
            return undefined;
    }
};
